/* eslint-disable */
/* tslint-disable */
// @ts-nocheck
import AdadaptedJsSdk from '@adadapted/js-sdk/src'
import { inject, Service, service } from './di'
import { EVTrackingService } from './EVTrackingService'
import { ConfigService } from './ConfigService'
import { LocationService } from './LocationService'
import { generateUUID, getSessionId } from '../utils/uuid'
import { EventService } from './EventService'
import { ShoppingListService, SLListItem } from './ShoppingListService'
import {
    ShoppingListAction,
    ShoppingListEventContext,
} from './definitions/IAnalyticsService'
import { AnalyticsService } from './AnalyticsService'
import { ReleaseService } from './ReleaseService'
import { keys, values } from 'lodash-es'
import { AdBlockerDetectionService } from './AdBlockerDetectionService'
import AvroAnalytics from './analytics/BrowseAvro'
import { SCROLLABLE_CONTAINER_ID } from '../components/flipp-page/Page'

export type FlyerListAdSlotConfig = {
    id: string
    zones: { [deviceType: string]: number }
}

export type FlyerListAdSlotsConfig = {
    [position: string]: FlyerListAdSlotConfig
}

export const FLYER_LIST_AD_SLOT_ZONE_NAME_BASE = 'flyer-list-ad-slot-zone'

const FLYER_LIST_AD_ADAPTED_NAME = 'flipp_web_flyer_list_ad_adapted'

const REQUIRED_IMPRESSION_TIME = 1000 //ms

export const FLYER_LIST_AD_SLOTS_CONFIG: FlyerListAdSlotsConfig = {
    '1': {
        id: `${FLYER_LIST_AD_ADAPTED_NAME}_1`,
        zones: { desktop: 102195, tablet: 102196, mobile: 102210 },
    },
    '7': {
        id: `${FLYER_LIST_AD_ADAPTED_NAME}_2`,
        zones: { desktop: 102208, tablet: 102209, mobile: 102211 },
    },
}

@service('adAdaptedService')
export class AdAdaptedService extends Service {
    @inject evTracker: EVTrackingService
    @inject config: ConfigService
    @inject location: LocationService
    @inject eventService: EventService
    @inject sl: ShoppingListService
    @inject analytics: AnalyticsService
    @inject releaseService: ReleaseService
    @inject adBlockerDetectionService: AdBlockerDetectionService

    private aaSdk: AdadaptedJsSdk
    private avro: AvroAnalytics = new AvroAnalytics()
    private currentAdSlotID = null
    private currentAdSlotZoneID = null
    private firedBeacons = {
        impression: new Map<string, boolean>(),
        viewableImpression: new Map<string, boolean>(),
    }
    private observerImpressionMap = new Map<string, IntersectionObserver>()
    private observerViewableImpressionMap = new Map<
        string,
        IntersectionObserver
    >()
    private _shouldDisplayAd = true

    async init() {
        if (!this.releaseService.shouldUseAdAdapted) return

        this.aaSdk = new AdadaptedJsSdk()
        this.setEmptyFiredBeacons()
        const localeArr = this.location.locale.split('-')
        const deviceLocale = `${localeArr[0]}-${localeArr[1].toUpperCase()}`

        const zonePlacements = keys(FLYER_LIST_AD_SLOTS_CONFIG).reduce(
            (acc: any, position: string) => {
                const item = FLYER_LIST_AD_SLOTS_CONFIG[position]
                values(item.zones).forEach((zoneId) => {
                    acc[zoneId] =
                        `${FLYER_LIST_AD_SLOT_ZONE_NAME_BASE}-${zoneId}`
                })
                return acc
            },
            {}
        )
        setTimeout(() => {
            this.initImpressionEventsObserver()
        })

        return this.aaSdk.initialize({
            apiKey: this.config.ADADAPTED_API_KEY,
            advertiserId: getSessionId(),
            allowRetargeting: true,
            deviceLocale,
            zonePlacements,
            apiEnv: this.config.ADADAPTED_ENV as any,
            scrollContainerId: SCROLLABLE_CONTAINER_ID,
            onAddItemsTriggered: (items) => {
                const names = items.map((item) => item.product_title)
                names.forEach((name) => {
                    this.sl.addItemToShoppingList(name)
                })
                this.eventService.displayAddedItemPopup(items.length)
                this.reportItemsAddedToList(names)
                this.aaSdk.acknowledgeAdded()
                const item = items.reduce((acc, item, currentIndex) => {
                    return currentIndex === 0
                        ? acc + item.product_title
                        : acc + ',' + item.product_title
                }, items.length.toString())
                this.analytics.trackShoppingListChanged(
                    item,
                    this.sl.localList.id,
                    ShoppingListAction.Add,
                    ShoppingListEventContext.AdAdapted
                )
                // Need to delay firing beacon by using setTimeout
                // to be able to catch clicked ad slot container ID
                setTimeout(() => {
                    const id = `${this.currentAdSlotID}`
                    const zoneId = `${this.currentAdSlotZoneID}`
                    this.avro.FireBrowseAdAdaptedOpenAddToShoppingListAd({
                        items,
                        id,
                        zoneId,
                    })
                    this.currentAdSlotID = null
                })
            },
            onExternalContentAdClicked: (adId: string) => {
                // Need to delay firing beacon by using setTimeout
                // to be able to catch clicked ad slot container ID
                setTimeout(() => {
                    const id = `${this.currentAdSlotID}`
                    const zoneId = `${this.currentAdSlotZoneID}`
                    this.avro.FireBrowseAdAdaptedOpenAddToCartAd({
                        adId,
                        id,
                        zoneId,
                    })
                    this.currentAdSlotID = null
                })
            },
            onAdsRetrieved: (adZoneAdAvailabilityMap) => {
                this.eventService.adLoaded(adZoneAdAvailabilityMap)
            },
        })
    }

    shouldDisplayAd() {
        return (
            this._shouldDisplayAd &&
            this.releaseService.shouldUseAdAdapted &&
            !this.adBlockerDetectionService.isDetected()
        )
    }

    disableAd() {
        this._shouldDisplayAd = false
    }

    async reportItemsAddedToList(items: string[], listName?: string) {
        if (!this.aaSdk) await this.init()
        this.aaSdk.reportItemsAddedToList(items, listName)
    }

    async reportItemsRemovedFromList(items: string[]) {
        if (!this.aaSdk) await this.init()
        this.aaSdk.reportItemsDeletedFromList(items)
    }

    setCurrentAdSlotData(id: string) {
        this.currentAdSlotID = id
        this.currentAdSlotZoneID = this.detectAdSlotCurrentZoneId(id)
    }

    setEmptyFiredBeacons() {
        values(FLYER_LIST_AD_SLOTS_CONFIG).forEach(
            (item: FlyerListAdSlotConfig) => {
                this.firedBeacons['impression'].set(item.id, false)
                this.firedBeacons['viewableImpression'].set(item.id, false)
            }
        )
    }

    private detectAdSlotCurrentZoneId(id: string): string {
        let zoneId
        const el = document.getElementById(id)
        const zoneSlotEls = el.children
        for (let item of zoneSlotEls) {
            if (item.checkVisibility()) {
                zoneId = item.id.split('-').pop()
            }
        }
        return zoneId
    }

    private onAdSlotVisibilityChange(entries, observer) {
        this.fireImpressionAvroBeacon({
            entry: entries[0],
            threshold: observer.thresholds[0],
        })
    }

    private fireImpressionAvroBeacon({ entry, threshold }) {
        const event = threshold === 0 ? 'impression' : 'viewableImpression'
        const id = entry.target.id
        if (entry.isIntersecting && !this.firedBeacons[event].get(id)) {
            const options = {
                zoneId: this.detectAdSlotCurrentZoneId(id),
                id,
            }
            if (threshold === 0) {
                this.avro.FireBrowseAdAdaptedImpressionAd(options)
                this.firedBeacons[event].set(id, true)
            } else {
                setTimeout(() => {
                    this.avro.FireBrowseAdAdaptedViewableImpressionAd(options)
                    this.firedBeacons[event].set(id, true)
                }, REQUIRED_IMPRESSION_TIME)
            }
        }
    }
    private initImpressionEventsObserver() {
        values(FLYER_LIST_AD_SLOTS_CONFIG).forEach(
            (item: FlyerListAdSlotConfig) => {
                const target = document.getElementById(item.id)
                if (target) {
                    this.getObserverImpression(item.id).observe(target)
                    this.getObserverViewableImpression(item.id).observe(target)
                }
            }
        )
    }

    private getObserverImpression(id: string) {
        if (!this.observerImpressionMap.get(id)) {
            const observerImpression = this.buildIntersectionObserver(0)
            this.observerImpressionMap.set(id, observerImpression)
        }
        return this.observerImpressionMap.get(id)
    }

    private getObserverViewableImpression(id: string) {
        if (!this.observerViewableImpressionMap.get(id)) {
            const observerViewableImpression =
                this.buildIntersectionObserver(0.5)
            this.observerViewableImpressionMap.set(
                id,
                observerViewableImpression
            )
        }
        return this.observerViewableImpressionMap.get(id)
    }

    private buildIntersectionObserver(threshold: number) {
        return new IntersectionObserver(
            this.onAdSlotVisibilityChange.bind(this),
            {
                root: document.getElementById(`#${SCROLLABLE_CONTAINER_ID}`),
                threshold,
            }
        )
    }
}
/* eslint-enable */
/* tslint-enable */
